import { useCallback, useState } from 'react';

import { useApolloClient } from '@apollo/client';

import { useSanityGqlEndpoint } from 'state/network';
import logger from 'utils/logger';

import {
  EngineOfferListItem,
  EnginePersonalizedOffer,
  isEnginePersonalizedOffer,
  isEnginePersonalizedOfferGroup,
} from '../types';

import {
  IPersonalizedData,
  fetchPersonalizedOfferData as fetchData,
} from './utils/personalized-offers';

export const usePersonalizedOffers: () => [
  (offers: readonly EngineOfferListItem[]) => Promise<IPersonalizedData | null>,
  { loading: boolean }
] = () => {
  const [loading, setLoading] = useState(false);

  const client = useApolloClient();
  const uri = useSanityGqlEndpoint();

  const fetchPersonalizedOfferData = useCallback(
    async (offers: EngineOfferListItem[]) => {
      let data = null;

      const standaloneConfigOffersIds = offers
        .filter(
          (offer): offer is EnginePersonalizedOffer =>
            isEnginePersonalizedOffer(offer) && !!offer.configId
        )
        .map(offer => offer.configId);

      const groupOfferConfigIds = offers
        .filter(isEnginePersonalizedOfferGroup)
        .flatMap(group => group.offers)
        .filter((offer): offer is EnginePersonalizedOffer => Boolean(offer?.configId))
        .map(offer => offer.configId);

      const offerGroupsIds = Array.from(
        new Set(
          offers
            .filter(isEnginePersonalizedOfferGroup)
            .map(group => group.cmsId)
            .filter(Boolean)
        )
      );

      const configOffersIds = Array.from(new Set(standaloneConfigOffersIds));
      const systemwideOffersIds = Array.from(new Set(groupOfferConfigIds));

      if (!configOffersIds.length && !systemwideOffersIds.length && !offerGroupsIds.length) {
        return data;
      }

      try {
        setLoading(true);
        data = await fetchData({
          client,
          uri,
          configOffersIds,
          systemwideOffersIds,
          offerGroupsIds,
        });
      } catch (e) {
        logger.error({ error: e, message: 'Error fetching personalized offers data' });
      } finally {
        setLoading(false);
      }

      return data;
    },
    [client, uri]
  );

  return [fetchPersonalizedOfferData, { loading }];
};
